import { toast } from "sonner";
import { useState, useRef, useEffect } from "react";
import { Sparkles, Loader2, Plus, X, RefreshCw, ImageIcon, Download, Maximize2, Film, Wand2, Clapperboard, Mic, Check, Upload, Eye, Lock, Trash2, Lightbulb, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { imageUrl, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CopyButton from "@/components/CopyButton";

const boxCls =
  "bg-[#0e1015] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0";

async function downloadImage(id, name) {
  try {
    const res = await fetch(imageUrl(id));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  } catch {}
}

function GeneratedImage({ id, label, testid, onRegenerate, onUpload, generating, approved, onToggleApprove }) {
  const upRef = useRef(null);
  const imgBlock = (
    <div className={`group relative h-full min-h-[130px] overflow-hidden rounded-md border ${approved ? "border-[#22c55e] ring-2 ring-[#22c55e]" : "border-[#272a35]"} bg-[#0e1015]`} data-testid={testid}>
      <img src={imageUrl(id)} alt={label} className="h-full w-full object-cover" />
      {approved && (
        <span className="absolute left-2 top-2 z-10 flex items-center gap-1 rounded bg-[#22c55e] px-1.5 py-0.5 text-[10px] font-bold text-black" data-testid={`${testid}-approved-badge`}>
          <Check className="h-3 w-3" /> APPROVED
        </span>
      )}
      {generating && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70">
          <Loader2 className="h-6 w-6 animate-spin text-[#00e5ff]" />
          <span className="text-xs text-[#00e5ff]">Working…</span>
        </div>
      )}
      <div className="absolute inset-0 flex items-end justify-end gap-1 bg-gradient-to-t from-black/80 to-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
        {approved ? (
          <span
            data-testid={`${testid}-regenerate-locked`}
            title="This image is approved and locked. Un-approve it below to regenerate or replace it."
            className="flex items-center gap-1 rounded bg-black/70 px-2 py-1.5 text-[10px] font-semibold text-[#22c55e]"
          >
            <Lock className="h-3.5 w-3.5" /> Locked
          </span>
        ) : (
          <button
            onClick={onRegenerate}
            disabled={generating}
            data-testid={`${testid}-regenerate`}
            title="Regenerate / edit this image"
            className="rounded bg-black/60 p-1.5 text-white hover:bg-black/80"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${generating ? "animate-spin" : ""}`} />
          </button>
        )}
        {onUpload && !approved && (
          <>
            <button
              onClick={() => upRef.current?.click()}
              disabled={generating}
              data-testid={`${testid}-upload`}
              title="Upload your own image"
              className="rounded bg-black/60 p-1.5 text-white hover:bg-black/80"
            >
              <Upload className="h-3.5 w-3.5" />
            </button>
            <input
              ref={upRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
            />
          </>
        )}
        <Dialog>
          <DialogTrigger asChild>
            <button className="rounded bg-black/60 p-1.5 text-white hover:bg-black/80" data-testid={`${testid}-enlarge`}>
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          </DialogTrigger>
          <DialogContent className="w-auto max-w-[95vw] border-[#272a35] bg-[#0b0c10] p-2">
            <img src={imageUrl(id)} alt={label} className="mx-auto block max-h-[85vh] w-auto max-w-full rounded object-contain" />
          </DialogContent>
        </Dialog>
        <button
          onClick={() => downloadImage(id, `${label}.png`)}
          data-testid={`${testid}-download`}
          className="rounded bg-black/60 p-1.5 text-white hover:bg-black/80"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
  if (!onToggleApprove) return imgBlock;
  return (
    <div className="flex h-full min-h-[130px] flex-col gap-1.5">
      <div className="min-h-[130px] flex-1">{imgBlock}</div>
      <button
        onClick={onToggleApprove}
        data-testid={`${testid}-approve`}
        className={`flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${approved ? "bg-[#22c55e] text-black hover:bg-[#16a34a]" : "border border-[#22c55e]/50 text-[#22c55e] hover:bg-[#22c55e]/10"}`}
        title={approved ? "Click to un-approve so you can regenerate/replace this image" : "Approve this image"}
      >
        <Check className="h-3.5 w-3.5" /> {approved ? "Approved — click to un-approve & edit" : "Approve image"}
      </button>
    </div>
  );
}

function GenerateBox({ onClick, onUpload, generating, disabled, testid, label }) {
  const upRef = useRef(null);
  return (
    <div
      className={`flex h-full min-h-[130px] w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed ${
        disabled ? "border-[#272a35] opacity-50" : "border-[#00e5ff]/50"
      } bg-[#0e1015] text-[#9ca3af] transition-colors`}
    >
      {generating ? (
        <>
          <Loader2 className="h-6 w-6 animate-spin text-[#00e5ff]" />
          <span className="text-xs text-[#00e5ff]">Generating…</span>
        </>
      ) : (
        <>
          <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            className="flex flex-col items-center gap-2 rounded px-3 py-1 hover:bg-[#00e5ff]/5"
          >
            <Wand2 className="h-6 w-6 text-[#00e5ff]" />
            <span className="text-xs font-medium text-[#f3f4f6]">{label}</span>
          </button>
          {onUpload && (
            <>
              <button
                onClick={() => upRef.current?.click()}
                disabled={disabled}
                data-testid={`${testid}-upload`}
                className="flex items-center gap-1 text-[11px] text-[#9ca3af] underline decoration-dotted hover:text-[#00e5ff]"
              >
                <Upload className="h-3 w-3" /> or upload your own
              </button>
              <input
                ref={upRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}

function VideoPlaceholder({ testid }) {
  return (
    <div
      data-testid={testid}
      className="flex h-full min-h-[130px] flex-col items-center justify-center gap-1.5 rounded-md border border-dashed border-[#272a35] bg-[#0e1015]/50 text-center"
    >
      <Film className="h-5 w-5 text-[#3a3f4d]" />
      <span className="text-xs font-medium text-[#6b7280]">Video</span>
      <span className="px-2 text-[10px] leading-tight text-[#4b5563]">Generate the image first</span>
    </div>
  );
}

function ElapsedTimer({ testid }) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setSecs(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");
  return (
    <span data-testid={testid} className="font-mono text-sm font-bold tabular-nums text-[#a855f7]">
      {mm}:{ss}
    </span>
  );
}

function SceneVideo({ url, hasImage, generating, onGenerate, onRegenerate, onUpload, testid, duration, resolution, model, onOpt, approved, onToggleApprove, isShort = false, onCancel }) {
  const selCls = "w-full rounded border border-[#272a35] bg-[#0b0c10] px-1.5 py-1 text-[10px] text-[#f3f4f6] focus:outline-none focus:ring-1 focus:ring-[#a855f7]";
  const upRef = useRef(null);
  const uploadInput = onUpload ? (
    <input
      ref={upRef}
      type="file"
      accept="video/mp4,video/quicktime,video/webm,video/*"
      className="hidden"
      data-testid={`${testid}-upload-input`}
      onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }}
    />
  ) : null;
  if (url) {
    return (
      <div className={`flex h-full min-h-[130px] flex-col gap-1.5 rounded-md ${generating ? "ring-2 ring-[#a855f7] animate-pulse" : approved ? "ring-2 ring-[#22c55e]" : ""}`} data-testid={`${testid}-player`}>
        <div className={`group relative overflow-hidden rounded-md border border-[#272a35] bg-black ${isShort ? "mx-auto aspect-[9/16] w-auto max-h-[70vh]" : "flex-1"}`}>
          <video src={url} controls playsInline className={`h-full w-full ${isShort ? "object-contain" : "object-cover"}`} />
          {generating && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/75 backdrop-blur-sm" data-testid={`${testid}-regenerating`}>
              <Loader2 className="h-7 w-7 animate-spin text-[#a855f7]" />
              <span className="text-xs font-bold uppercase tracking-wide text-[#a855f7]">Regenerating…</span>
              <ElapsedTimer testid={`${testid}-timer`} />
              <span className="px-3 text-center text-[10px] text-[#d1d5db]">Replacing this video — can take a few minutes</span>
            </div>
          )}
          {approved && !generating && (
            <span className="absolute left-2 top-2 flex items-center gap-1 rounded bg-[#22c55e] px-1.5 py-0.5 text-[10px] font-bold text-black" data-testid={`${testid}-approved-badge`}>
              <Check className="h-3 w-3" /> APPROVED
            </span>
          )}
          <a
            href={url}
            download={`${testid}.mp4`}
            data-testid={`${testid}-download`}
            title="Download video"
            className="absolute right-2 top-2 rounded bg-black/60 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100 hover:bg-black/80"
          >
            <Download className="h-3.5 w-3.5" />
          </a>
        </div>
        <button
          onClick={onToggleApprove}
          data-testid={`${testid}-approve`}
          className={`flex w-full items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${approved ? "bg-[#22c55e] text-black hover:bg-[#16a34a]" : "border border-[#22c55e]/50 text-[#22c55e] hover:bg-[#22c55e]/10"}`}
          title={approved ? "Click to un-approve so you can regenerate/edit this video" : "Approve this video for the final cut"}
        >
          <Check className="h-3.5 w-3.5" /> {approved ? "Approved — click to un-approve & edit" : "Approve for final cut"}
        </button>
        {approved ? (
          <button
            disabled
            data-testid={`${testid}-regenerate-locked`}
            title="This video is approved and locked. Un-approve it above to regenerate."
            className="flex w-full cursor-not-allowed items-center justify-center gap-1.5 rounded-md border border-[#3a3d46] bg-[#1a1c22] px-2 py-1.5 text-xs font-semibold text-[#6b7280]"
          >
            <Lock className="h-3.5 w-3.5" /> Locked — un-approve to regenerate
          </button>
        ) : (
          <>
            <button
              onClick={() => onRegenerate(duration, resolution, model)}
              disabled={generating}
              data-testid={`${testid}-regenerate`}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-[#a855f7]/50 bg-[#a855f7]/10 px-2 py-1.5 text-xs font-semibold text-[#a855f7] hover:bg-[#a855f7]/20 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${generating ? "animate-spin" : ""}`} /> Regenerate
            </button>
            {onUpload && (
              <button
                onClick={() => upRef.current?.click()}
                disabled={generating}
                data-testid={`${testid}-upload`}
                title="Replace this video with one you made in another tool (mp4, mov or webm)"
                className="flex w-full items-center justify-center gap-1.5 rounded-md border border-[#272a35] px-2 py-1.5 text-xs font-semibold text-[#9ca3af] hover:border-[#00e5ff]/50 hover:text-[#00e5ff] disabled:opacity-50"
              >
                <Upload className="h-3.5 w-3.5" /> Upload replacement
              </button>
            )}
          </>
        )}
        {uploadInput}
      </div>
    );
  }
  if (!hasImage) return <VideoPlaceholder testid={testid} />;
  return (
    <div className="flex h-full min-h-[130px] w-full flex-col items-center justify-center gap-2 rounded-md border border-dashed border-[#a855f7]/50 bg-[#0e1015] p-2" data-testid={`${testid}-box`}>
      {generating ? (
        <div className="flex flex-col items-center gap-1.5 py-2">
          <Loader2 className="h-6 w-6 animate-spin text-[#a855f7]" />
          <span className="text-xs text-[#a855f7]">Rendering video…</span>
          <ElapsedTimer testid={`${testid}-timer`} />
          <span className="px-2 text-center text-[10px] text-[#6b7280]">This can take a few minutes (auto-stops after 12 min)</span>
          {onCancel && (
            <button
              onClick={onCancel}
              data-testid={`${testid}-cancel`}
              title="Stop generating this scene's video"
              className="mt-1 inline-flex items-center gap-1 rounded-md border border-[#ef4444]/50 px-2 py-1 text-[10px] text-[#f87171] hover:bg-[#ef4444]/10 transition-colors"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
          )}
        </div>
      ) : approved ? (
        <div className="flex flex-col items-center gap-2 py-2 text-center">
          <Lock className="h-5 w-5 text-[#22c55e]" />
          <span className="px-2 text-[10px] text-[#9ca3af]">This scene is approved &amp; locked. Un-approve it to generate or regenerate the video.</span>
          <button
            onClick={onToggleApprove}
            data-testid={`${testid}-approve`}
            title="Un-approve so you can generate/regenerate this video"
            className="flex w-full items-center justify-center gap-1.5 rounded-md bg-[#22c55e] px-2 py-1.5 text-xs font-semibold text-black hover:bg-[#16a34a]"
          >
            <Check className="h-3.5 w-3.5" /> Approved — click to un-approve
          </button>
        </div>
      ) : (
        <>
          <Film className="h-5 w-5 text-[#a855f7]" />
          <select data-testid={`${testid}-model`} value={model} onChange={(e) => onOpt("model", e.target.value)} className={selCls}>
            <option value="1.5">Seedance 1.5 Pro</option>
            <option value="2.0">Seedance 2.0</option>
            <option value="2.5">Seedance 2.5</option>
            <option value="grok">Grok Imagine</option>
            <option value="runway">Runway (Gen-4)</option>
            <option value="runway_seedance">Runway · Seedance 2.5</option>
            <option value="runway_hailuo">Runway · MiniMax 3</option>
          </select>
          <div className="flex w-full gap-1">
            <select data-testid={`${testid}-duration`} value={duration} onChange={(e) => onOpt("duration", Number(e.target.value))} className={selCls}>
              <option value={3}>3s</option>
              <option value={5}>5s</option>
              <option value={8}>8s</option>
              <option value={10}>10s</option>
              <option value={12}>12s</option>
              <option value={15}>15s</option>
              <option value={20}>20s</option>
              <option value={30}>30s</option>
            </select>
            <select data-testid={`${testid}-resolution`} value={resolution} onChange={(e) => onOpt("resolution", e.target.value)} className={selCls}>
              <option value="480p">480p</option>
              <option value="720p">720p</option>
              <option value="1080p">1080p</option>
            </select>
          </div>
          <button
            onClick={() => onGenerate(duration, resolution, model)}
            data-testid={testid}
            className="w-full rounded-md bg-[#a855f7] px-2 py-1.5 text-xs font-semibold text-white hover:bg-[#9333ea]"
          >
            Generate video
          </button>
          {onUpload && (
            <button
              onClick={() => upRef.current?.click()}
              data-testid={`${testid}-upload`}
              title="Upload a video you made in another tool (mp4, mov or webm)"
              className="flex items-center gap-1 text-[11px] text-[#9ca3af] underline decoration-dotted hover:text-[#00e5ff]"
            >
              <Upload className="h-3 w-3" /> or upload your own
            </button>
          )}
          <span className="text-[10px] text-[#6b7280]">{model === "grok" ? "Grok Imagine · native audio · up to 15s" : model === "runway" ? "Runway Gen-4 · 5s or 10s · no audio" : model === "runway_seedance" ? "Runway · Seedance 2.5 · 4–30s · 480p/720p" : model === "runway_hailuo" ? "Runway · MiniMax Hailuo 3 · 5–15s · 768P/2K" : model === "2.5" ? "Native audio · up to 30s · up to 4K" : model === "2.0" ? "Voice + lip-sync · up to 15s" : "Voice + lip-sync · up to 12s"}</span>
        </>
      )}
      {uploadInput}
    </div>
  );
}

function NicheSuggestions({ channel, onPick }) {
  const [openId, setOpenId] = useState(null);
  const niches = (channel?.saved_niches || []).filter(
    (n) => (n.video_ideas || []).length || (n.sample_titles || []).length,
  );
  if (!niches.length) return null;
  // Default the first niche open.
  const activeId = openId || niches[0].niche_id;
  return (
    <div className="rounded-lg border border-[#00e5ff]/25 bg-[#00e5ff]/[0.04] p-3" data-testid="niche-suggestions">
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-[#00e5ff]">
        <Lightbulb className="h-3.5 w-3.5" />
        Ideas from “{channel.name || "this channel"}” saved niches
      </div>
      <div className="space-y-1.5">
        {niches.map((n) => {
          const isOpen = n.niche_id === activeId;
          const titles = (n.sample_titles || []).slice(0, 4);
          const ideas = (n.video_ideas || []).slice(0, 8);
          return (
            <div key={n.niche_id} className="rounded-md border border-[#272a35] bg-[#0e1015]">
              <button
                type="button"
                data-testid={`niche-suggestion-toggle-${n.niche_id}`}
                onClick={() => setOpenId(isOpen ? "__none__" : n.niche_id)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
              >
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-[#9ca3af]" /> : <ChevronRight className="h-3.5 w-3.5 text-[#9ca3af]" />}
                <span className="flex-1 truncate text-xs font-medium text-[#f3f4f6]">{n.name}</span>
                {n.opportunity_score != null && (
                  <span className="rounded bg-[#1d2028] px-1.5 py-0.5 text-[10px] font-semibold text-[#22c55e]">{n.opportunity_score}</span>
                )}
              </button>
              {isOpen && (
                <div className="space-y-2 border-t border-[#272a35] px-3 py-2.5">
                  {titles.length > 0 && (
                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-[#6b7280]">Sample titles</p>
                      <div className="flex flex-wrap gap-1.5">
                        {titles.map((t, i) => (
                          <button
                            key={i}
                            type="button"
                            data-testid={`niche-suggestion-title-${n.niche_id}-${i}`}
                            onClick={() => onPick(t)}
                            className="rounded-full border border-[#272a35] bg-[#14161c] px-2.5 py-1 text-[11px] text-[#d1d5db] transition-colors hover:border-[#00e5ff]/50 hover:text-[#00e5ff]"
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {ideas.length > 0 && (
                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-[#6b7280]">Video ideas</p>
                      <div className="space-y-1">
                        {ideas.map((v, i) => (
                          <button
                            key={i}
                            type="button"
                            data-testid={`niche-suggestion-idea-${n.niche_id}-${i}`}
                            onClick={() => onPick(v)}
                            className="flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-left text-[11px] text-[#d1d5db] transition-colors hover:bg-[#1d2028] hover:text-[#00e5ff]"
                          >
                            <Plus className="h-3 w-3 shrink-0 text-[#6b7280]" />
                            <span className="min-w-0 flex-1">{v}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function StoryBoard({
  project, setField, onGenerateText, generating, onGenerateThumbnail, onGenerateThumbnailVideo, onToggleThumbnailApprove, onGenerateScene, onGenerateVideo, onCancelVideo,
  onGenerateDialogs, onToggleApprove, onToggleImageApprove, onUploadImage, onUploadVideo, onEditImage, onComposePrompt, onGenerateAllVideos, onAssembleVideo, assembling, assembleDone = false, assembleProgress, assembleElapsed = 0, assembleLogs = [], assembleError = "", onDismissAssembleError, finalVideoUrl, finalVideoDuration = 0, channels = [], onSelectChannel, regenKeys = [],
}) {
  const scenes = project.scenes || [];
  const sceneImageIds = project.scene_image_ids || [];
  const sceneVideoUrls = project.scene_video_urls || [];
  const sceneDialogs = project.scene_dialogs || [];
  const sceneApproved = project.scene_approved || [];
  const sceneImageApproved = project.scene_image_approved || [];
  const [vopts, setVopts] = useState({});
  const [tvopt, setTvopt] = useState({ duration: 3, resolution: "720p", model: "1.5" });
  const getOpt = (i) => ({
    duration: vopts[i]?.duration ?? 10,
    resolution: project.video_resolution || "720p",
    model: project.video_model || "1.5",
  });
  const setOpt = (i, field, val) => {
    // Model + resolution are STORY-LEVEL (persisted on the project) so every scene stays the same
    // size and the picker actually changes the provider used by the backend. Duration is per-scene.
    if (field === "model") return setField("video_model", val);
    if (field === "resolution") return setField("video_resolution", val);
    setVopts((o) => ({ ...o, [i]: { ...(o[i] || {}), [field]: val } }));
  };
  const anyVideoBusy = (regenKeys || []).some((k) => k.startsWith("video-"));
  const approvedCount = sceneVideoUrls.filter((u, i) => u && sceneApproved[i]).length;
  const scenesWithImages = scenes.filter((_, i) => sceneImageIds[i]).length;
  const [dlg, setDlg] = useState(null); // { kind: "thumbnail"|"scene", index }
  const [mods, setMods] = useState("");
  const [selChars, setSelChars] = useState([]);
  const [promptView, setPromptView] = useState(null); // exact composed prompt (editable) or null
  const [promptLoading, setPromptLoading] = useState(false);
  const exactPromptRef = useRef(null);
  const [vdlg, setVdlg] = useState(null); // { index, duration, resolution, model }
  const [skipIntro, setSkipIntro] = useState(false);
  const { user } = useAuth();
  const canViewAssembleLog = (user?.email || "").toLowerCase() === "c130usmc@gmail.com";
  const [vmods, setVmods] = useState("");
  const characters = (() => {
    const seen = new Set();
    const out = [];
    const chRefs = (channels.find((c) => c.channel_id === project.channel_id)?.references) || [];
    // Merge the story's own references with the assigned channel's CURRENT references, so
    // characters added to the channel AFTER this story was created (e.g. "Doctor") still show
    // up here and can be locked onto a scene. Project refs win on a name clash.
    for (const r of [...(project.references || []), ...chRefs]) {
      if (r.type !== "character" || !r.image_id) continue;
      const k = (r.label || "").trim().toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(r);
    }
    return out;
  })();

  const openVideoRegen = (index, duration, resolution, model) => { setVmods(""); setVdlg({ index, duration, resolution, model }); };
  const confirmVideoRegen = () => {
    if (!vdlg) return;
    onGenerateVideo(vdlg.index, vdlg.duration, vdlg.resolution, vdlg.model, vmods);
    setVdlg(null);
  };

  const busy = (key) => (regenKeys || []).includes(key);
  const updateScene = (i, val) => { const n = [...scenes]; n[i] = val; setField("scenes", n); };
  const updateDialog = (i, val) => { const n = [...sceneDialogs]; while (n.length <= i) n.push(""); n[i] = val; setField("scene_dialogs", n); };
  const addScene = () => setField("scenes", [...scenes, ""]);
  const removeScene = (i) => setField("scenes", scenes.filter((_, idx) => idx !== i));

  const openRegen = (kind, index) => {
    setMods("");
    setPromptView(null);
    if (kind === "thumbnail") {
      // Thumbnails should feature the whole cast — pre-select ALL available characters.
      setSelChars(characters.map((c) => c.image_id));
    } else {
      // Pre-select characters whose label appears in the scene text.
      const text = (scenes[index] || "").toLowerCase();
      setSelChars(characters.filter((c) => c.label && text.includes(c.label.toLowerCase())).map((c) => c.image_id));
    }
    setDlg({ kind, index });
  };
  const toggleChar = (id) => setSelChars((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const loadExactPrompt = async () => {
    if (!dlg || !onComposePrompt) return;
    setPromptLoading(true);
    try {
      const p = await onComposePrompt(dlg.kind, dlg.index, mods, selChars);
      setPromptView(p || "");
    } finally {
      setPromptLoading(false);
    }
  };
  const confirmRegen = () => {
    if (!dlg) return;
    const text = (mods || "").trim();
    const hasImage = dlg.kind === "thumbnail"
      ? !!project.thumbnail_image_id
      : !!(project.scene_image_ids || [])[dlg.index];
    // The effective prompt: the edited exact-prompt if the user opened it, otherwise their quick text.
    const edited = promptView != null ? promptView.trim() : "";
    const instruction = edited || text;
    if (hasImage && instruction) {
      // An image already exists -> EDIT it: send the CURRENT image + this prompt to make the change.
      onEditImage(dlg.kind, dlg.index, instruction);
    } else if (dlg.kind === "thumbnail") {
      // No image yet -> generate from scratch (use edited exact prompt as override if provided).
      onGenerateThumbnail(mods, selChars, edited || undefined);
    } else {
      onGenerateScene(dlg.index, mods, selChars, edited || undefined);
    }
    setDlg(null);
  };

  const hasContent = project.title || project.description || scenes.length > 0;
  const isEmpty = !(project.idea || "").trim() && !hasContent;
  const needsChannel = !project.channel_id;
  const fmtDur = (s) => {
    s = Math.max(0, Math.round(s || 0));
    const m = Math.floor(s / 60);
    return `${m}:${String(s % 60).padStart(2, "0")}`;
  };
  const dlgHasImage = dlg && (dlg.kind === "thumbnail"
    ? !!project.thumbnail_image_id
    : !!(project.scene_image_ids || [])[dlg.index]);

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <p className="overline">Step 1 — Idea &amp; script</p>
      <h2 className="font-display mt-1 text-lg font-semibold tracking-tight">Story &amp; storyboard</h2>

      <div className="mt-5">
        <Label className="text-sm text-[#9ca3af]">
          Channel <span className="text-[#00e5ff]">*</span>
        </Label>
        <select
          data-testid="channel-select"
          value={project.channel_id || ""}
          disabled={hasContent}
          onChange={(e) => onSelectChannel && onSelectChannel(e.target.value)}
          className={`mt-1.5 w-full rounded-lg border bg-[#0e1015] px-3 py-2 text-sm text-[#f3f4f6] transition-colors ${needsChannel ? "border-[#00e5ff] ring-1 ring-[#00e5ff]/40" : "border-[#272a35]"} ${hasContent ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <option value="" disabled>Select a channel for this story…</option>
          {channels.map((c) => (
            <option key={c.channel_id} value={c.channel_id}>{c.name}</option>
          ))}
        </select>
        {channels.length === 0 ? (
          <p className="mt-1.5 text-[11px] text-[#f59e0b]">No channels yet — create one from the sidebar first.</p>
        ) : needsChannel ? (
          <p className="mt-1.5 text-[11px] text-[#9ca3af]">Choose a channel to apply its style, tone &amp; character references.</p>
        ) : null}
      </div>

      <div className="mt-5">
        <Label className="text-sm text-[#9ca3af]">Video format</Label>
        <div className="mt-1.5 flex gap-2" data-testid="format-selector">
          {[
            ["long", "Long Form", "16:9 · YouTube · any length"],
            ["short", "Short Form", "9:16 · Shorts/Reels · under 3 min"],
          ].map(([val, title, sub]) => {
            const active = (project.format || "long") === val;
            const locked = hasContent;
            return (
              <button
                key={val}
                type="button"
                data-testid={`format-${val}`}
                disabled={locked && !active}
                onClick={() => !locked && setField("format", val)}
                className={`flex-1 rounded-lg border px-3 py-2 text-left transition-colors ${active ? "border-[#00e5ff] bg-[#00e5ff]/10" : "border-[#272a35] hover:bg-[#1d2028]"} ${locked && !active ? "cursor-not-allowed opacity-40" : ""}`}
              >
                <div className={`text-sm font-semibold ${active ? "text-[#00e5ff]" : "text-[#f3f4f6]"}`}>{title}</div>
                <div className="text-[11px] text-[#9ca3af]">{sub}</div>
              </button>
            );
          })}
        </div>
        {hasContent && (
          <p className="mt-1.5 text-[11px] text-[#6b7280]">Format is locked once a story has content. Start a new story to change it.</p>
        )}
      </div>

      <div className="mt-5 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm text-[#9ca3af]">Your YouTube story idea</Label>
          {isEmpty && (
            <span data-testid="start-here-badge" className="start-here-badge flex items-center gap-1 rounded-full bg-[#00e5ff] px-2.5 py-0.5 text-[11px] font-bold text-black">
              <Sparkles className="h-3 w-3" /> Start here
            </span>
          )}
        </div>
        <Textarea
          data-testid="idea-input"
          value={project.idea || ""}
          onChange={(e) => setField("idea", e.target.value)}
          placeholder="e.g. A documentary-style story about a lighthouse keeper who discovers a message in a bottle that changes his life..."
          className={`min-h-[110px] resize-y ${boxCls} ${isEmpty ? "start-here-ring border-[#00e5ff]" : ""}`}
        />
        {project.channel_id && !(project.idea || "").trim() && (
          <NicheSuggestions
            channel={channels.find((c) => c.channel_id === project.channel_id)}
            onPick={(text) => setField("idea", text)}
          />
        )}
        <Button
          data-testid="generate-text-btn"
          onClick={onGenerateText}
          disabled={generating || !(project.idea || "").trim() || needsChannel}
          className="rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold"
        >
          {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
          {generating ? "Generating…" : "Generate title, description & scenes"}
        </Button>
        {needsChannel && (project.idea || "").trim() && (
          <p className="text-[11px] text-[#f59e0b]" data-testid="channel-required-hint">Select a channel above to continue.</p>
        )}
      </div>

      {hasContent && (
        <div className="mt-8 space-y-6 border-t border-[#272a35] pt-6">
          {/* Title */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-[#9ca3af]">SEO Title</Label>
              <CopyButton text={project.title} label="Title" testid="copy-title" />
            </div>
            <Input
              data-testid="title-input"
              value={project.title || ""}
              onChange={(e) => setField("title", e.target.value)}
              className={boxCls}
            />
          </div>

          {/* Thumbnail — under title, above description */}
          <div className="space-y-2">
            <Label className="text-sm text-[#9ca3af]">Thumbnail</Label>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-stretch">
              <div className={project.format === "short" ? "aspect-[9/16] w-full max-w-[260px]" : "aspect-video w-full max-w-xl"}>
                {project.thumbnail_image_id ? (
                  <GeneratedImage
                    id={project.thumbnail_image_id}
                    label="Thumbnail"
                    testid="gallery-thumbnail"
                    onRegenerate={() => openRegen("thumbnail", 0)}
                    onUpload={(f) => onUploadImage(f, "thumbnail", 0)}
                    generating={busy("thumbnail-0")}
                  />
                ) : (
                  <GenerateBox
                    testid="generate-thumbnail-btn"
                    onClick={() => onGenerateThumbnail("")}
                    onUpload={(f) => onUploadImage(f, "thumbnail", 0)}
                    generating={busy("thumbnail-0")}
                    disabled={!project.title && !project.description}
                    label="Generate thumbnail"
                  />
                )}
              </div>
              {/* Thumbnail video — a short clip generated FROM the thumbnail (same Seedance flow) */}
              <div className="w-full shrink-0 space-y-1.5 lg:w-72" data-testid="thumbnail-video-panel">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#6b7280]">
                  <Film className="h-3.5 w-3.5 text-[#a855f7]" /> Thumbnail video <span className="font-normal normal-case text-[#4b5563]">(optional)</span>
                </span>
                <SceneVideo
                  testid="thumbnail-video"
                  url={project.thumbnail_video_url ? `${API}/thumbnail-video/${project.project_id}?t=${encodeURIComponent(project.thumbnail_video_url)}` : null}
                  hasImage={!!project.thumbnail_image_id}
                  generating={busy("thumbnail-video")}
                  duration={tvopt.duration}
                  resolution={tvopt.resolution}
                  model={tvopt.model}
                  onOpt={(f, v) => setTvopt((o) => ({ ...o, [f]: v }))}
                  onGenerate={(d, r, m) => onGenerateThumbnailVideo(d, r, m)}
                  onRegenerate={(d, r, m) => onGenerateThumbnailVideo(d, r, m)}
                  approved={!!project.thumbnail_video_approved}
                  onToggleApprove={onToggleThumbnailApprove}
                  isShort={project.format === "short"}
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm text-[#9ca3af]">Description</Label>
              <CopyButton text={project.description} label="Description" testid="copy-description" />
            </div>
            <Textarea
              data-testid="description-input"
              value={project.description || ""}
              onChange={(e) => setField("description", e.target.value)}
              className={`min-h-[150px] resize-y ${boxCls}`}
            />
          </div>

          {/* Scenes */}
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label className="text-sm text-[#9ca3af]">Scenes ({scenes.length})</Label>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  data-testid="generate-dialogs-btn"
                  onClick={onGenerateDialogs}
                  disabled={generating || scenes.length === 0}
                  className="flex items-center gap-1.5 rounded-md border border-[#a855f7]/50 px-2.5 py-1 text-xs font-medium text-[#c084fc] transition-colors hover:bg-[#a855f7]/10 disabled:opacity-40"
                >
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mic className="h-3.5 w-3.5" />}
                  Generate dialog
                </button>
                <button
                  data-testid="generate-all-videos-btn"
                  onClick={onGenerateAllVideos}
                  disabled={anyVideoBusy || scenesWithImages === 0}
                  className="flex items-center gap-1.5 rounded-md border border-[#a855f7]/50 px-2.5 py-1 text-xs font-medium text-[#c084fc] transition-colors hover:bg-[#a855f7]/10 disabled:opacity-40"
                >
                  {anyVideoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Film className="h-3.5 w-3.5" />}
                  Generate all videos
                </button>
                <button
                  data-testid="assemble-video-btn"
                  onClick={() => onAssembleVideo(skipIntro)}
                  disabled={assembling || approvedCount === 0}
                  className="flex items-center gap-1.5 rounded-md border border-[#00e5ff]/50 px-2.5 py-1 text-xs font-medium text-[#00e5ff] transition-colors hover:bg-[#00e5ff]/10 disabled:opacity-40"
                >
                  {assembling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clapperboard className="h-3.5 w-3.5" />}
                  Assemble full video{approvedCount > 0 ? ` (${approvedCount})` : ""}
                </button>
                {project.thumbnail_video_approved && project.thumbnail_video_url && (
                  <label
                    data-testid="skip-intro-toggle"
                    title="Assemble scenes only, without prepending the approved thumbnail-video intro"
                    className="flex cursor-pointer items-center gap-1.5 rounded-md border border-[#374151] px-2.5 py-1 text-xs text-[#9ca3af] transition-colors hover:text-[#f3f4f6]"
                  >
                    <input
                      type="checkbox"
                      checked={skipIntro}
                      onChange={(e) => setSkipIntro(e.target.checked)}
                      disabled={assembling}
                      className="h-3.5 w-3.5 accent-[#00e5ff]"
                      data-testid="skip-intro-checkbox"
                    />
                    Skip intro
                  </label>
                )}
                <button
                  data-testid="add-scene-btn"
                  onClick={addScene}
                  className="flex items-center gap-1 text-xs text-[#9ca3af] hover:text-[#f3f4f6]"
                >
                  <Plus className="h-3.5 w-3.5" /> Add scene
                </button>
              </div>
            </div>

            {(assembling || assembleError || assembleDone) && (
              <div className={`space-y-1.5 rounded-md border p-3 ${assembleError ? "border-red-500/40 bg-red-500/[0.06]" : assembleDone ? "border-emerald-500/40 bg-emerald-500/[0.05]" : "border-[#00e5ff]/30 bg-[#00e5ff]/[0.04]"}`} data-testid="assemble-progress">
                <div className="flex items-center justify-between text-xs">
                  {assembleError ? (
                    <span className="flex items-center gap-1.5 font-medium text-red-400" data-testid="assemble-error-msg">
                      Assembly failed: {assembleError}
                    </span>
                  ) : assembleDone ? (
                    <span className="flex items-center gap-1.5 font-medium text-emerald-400" data-testid="assemble-done-msg">
                      <Check className="h-3.5 w-3.5" /> Assembly complete — log kept below
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-[#00e5ff]">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> {assembleProgress?.stage || "Assembling…"}
                    </span>
                  )}
                  <span className="flex items-center gap-2 font-mono text-[#00e5ff]">
                    {assembling && <span data-testid="assemble-elapsed">{fmtDur(assembleElapsed)}</span>}
                    {assembling && <span data-testid="assemble-progress-pct">{assembleProgress?.pct ?? 0}%</span>}
                    {(assembleError || assembleDone) && (
                      <button
                        onClick={onDismissAssembleError}
                        className={`rounded border px-2 py-0.5 text-[11px] ${assembleError ? "border-red-500/40 text-red-300 hover:bg-red-500/10" : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"}`}
                        data-testid="assemble-dismiss"
                      >Close</button>
                    )}
                  </span>
                </div>
                {assembling && (
                  <div className="h-2 w-full overflow-hidden rounded-full border border-[#272a35] bg-[#0e1015]">
                    <div
                      className="h-full rounded-full bg-[#00e5ff] transition-[width] duration-500 ease-out"
                      style={{ width: `${assembleProgress?.pct ?? 0}%` }}
                      data-testid="assemble-progress-bar"
                    />
                  </div>
                )}
                {canViewAssembleLog && assembleLogs && assembleLogs.length > 0 && (
                  <>
                    {(assembleError || assembleDone) && (
                      <button
                        onClick={() => { try { navigator.clipboard.writeText(assembleLogs.join("\n")); toast.success("Log copied"); } catch (e) {} }}
                        className="mt-1 rounded border border-[#272a35] px-2 py-0.5 text-[11px] text-[#9fb4c7] hover:bg-white/5"
                        data-testid="assemble-log-copy"
                      >Copy full log</button>
                    )}
                    <div
                      className="mt-2 max-h-56 overflow-y-auto rounded border border-[#272a35] bg-[#07080b] p-2 font-mono text-[11px] leading-relaxed text-[#9fb4c7]"
                      data-testid="assemble-log"
                      ref={(el) => { if (el && assembling) el.scrollTop = el.scrollHeight; }}
                    >
                      {assembleLogs.map((line, i) => (
                        <div key={i} className={line.includes("✗") || line.includes("⚠") ? "text-red-400" : line.includes("✓") ? "text-emerald-400" : ""}>
                          {line}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {finalVideoUrl && (
              <div className="space-y-2 rounded-md border border-[#00e5ff]/30 bg-[#00e5ff]/[0.04] p-3" data-testid="final-video-block-inprogress">
                <div className="flex items-center gap-2 text-sm font-medium text-[#f3f4f6]">
                  <Clapperboard className="h-4 w-4 text-[#00e5ff]" /> Full video ready — scroll to the bottom to watch &amp; download.
                </div>
              </div>
            )}

            {scenes.map((s, i) => {
              const key = `scene-${i}`;
              const imgId = sceneImageIds[i];
              return (
                <div
                  key={i}
                  data-testid={`scene-row-${i}`}
                  className="grid grid-cols-1 gap-4 border-t border-[#272a35] pt-4 lg:grid-cols-[220px_1fr_180px]"
                >
                  {/* Scene image */}
                  <div>
                    {imgId ? (
                      <GeneratedImage
                        id={imgId}
                        label={`Scene ${i + 1}`}
                        testid={`gallery-scene-${i}`}
                        onRegenerate={() => openRegen("scene", i)}
                        onUpload={(f) => onUploadImage(f, "scene", i)}
                        generating={busy(key)}
                        approved={!!sceneImageApproved[i]}
                        onToggleApprove={() => onToggleImageApprove(i)}
                      />
                    ) : (
                      <GenerateBox
                        testid={`generate-scene-${i}`}
                        onClick={() => onGenerateScene(i, "")}
                        onUpload={(f) => onUploadImage(f, "scene", i)}
                        generating={busy(key)}
                        disabled={!(s || "").trim()}
                        label="Generate image"
                      />
                    )}
                  </div>

                  {/* Scene description */}
                  <div className="relative">
                    <div className="absolute left-3 top-3 font-mono text-xs text-[#00e5ff]">{String(i + 1).padStart(2, "0")}</div>
                    <Textarea
                      data-testid={`scene-input-${i}`}
                      value={s}
                      onChange={(e) => updateScene(i, e.target.value)}
                      className={`min-h-[100px] resize-y pl-10 pr-16 ${boxCls}`}
                    />
                    <div className="absolute right-2 top-2 flex items-center gap-1">
                      <CopyButton text={s} label={`Scene ${i + 1}`} testid={`copy-scene-${i}`} />
                      <button
                        data-testid={`remove-scene-${i}`}
                        onClick={() => removeScene(i)}
                        className="text-[#9ca3af] hover:text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-start gap-2 rounded-md border border-[#a855f7]/30 bg-[#a855f7]/[0.05] p-2">
                      <Mic className="mt-1.5 h-3.5 w-3.5 shrink-0 text-[#a855f7]" />
                      <div className="flex-1">
                        <Label className="text-[10px] font-semibold uppercase tracking-wide text-[#a855f7]">Dialog / voiceover</Label>
                        <Textarea
                          data-testid={`scene-dialog-${i}`}
                          value={sceneDialogs[i] || ""}
                          onChange={(e) => updateDialog(i, e.target.value)}
                          placeholder="What the character says out loud in this scene…"
                          className={`mt-1 min-h-[44px] resize-y text-sm ${boxCls}`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Scene video (Seedance) */}
                  <SceneVideo
                    testid={`scene-video-${i}`}
                    url={sceneVideoUrls[i] ? `${API}/scene-video/${project.project_id}/${i}?t=${encodeURIComponent(sceneVideoUrls[i])}` : null}
                    hasImage={!!imgId}
                    generating={busy(`video-${i}`)}
                    duration={getOpt(i).duration}
                    resolution={getOpt(i).resolution}
                    model={getOpt(i).model}
                    onOpt={(f, v) => setOpt(i, f, v)}
                    onGenerate={(d, r, m) => onGenerateVideo(i, d, r, m)}
                    onCancel={() => onCancelVideo && onCancelVideo(i)}
                    onRegenerate={(d, r, m) => openVideoRegen(i, d, r, m)}
                    onUpload={(f) => onUploadVideo(i, f)}
                    approved={!!sceneApproved[i]}
                    onToggleApprove={() => onToggleApprove(i)}
                    isShort={project.format === "short"}
                  />
                </div>
              );
            })}

            {finalVideoUrl && (
              <div className="space-y-3 rounded-lg border border-[#00e5ff]/40 bg-[#00e5ff]/[0.05] p-4" data-testid="final-video-block">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-base font-semibold text-[#f3f4f6]">
                    <Clapperboard className="h-5 w-5 text-[#00e5ff]" /> Assembled full video
                    {finalVideoDuration > 0 && (
                      <span data-testid="final-video-duration" className="rounded-full border border-[#272a35] bg-[#0e1015] px-2 py-0.5 font-mono text-xs text-[#9ca3af]">
                        {fmtDur(finalVideoDuration)}
                      </span>
                    )}
                  </span>
                  <a
                    href={finalVideoUrl}
                    download="full-video.mp4"
                    data-testid="download-final-video"
                    className="flex items-center gap-1.5 rounded-md bg-[#00e5ff] px-3 py-1.5 text-sm font-semibold text-black transition-colors hover:bg-[#33ebff]"
                  >
                    <Download className="h-4 w-4" /> Download video
                  </a>
                </div>
                <video src={finalVideoUrl} controls className="w-full rounded-md border border-[#272a35] bg-black" data-testid="final-video-player" />
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="border-[#272a35] bg-[#14161c] text-[#f3f4f6]" data-testid="regen-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {dlg?.kind === "thumbnail" ? "Regenerate thumbnail" : `Regenerate scene ${(dlg?.index ?? 0) + 1} image`}
            </DialogTitle>
            <DialogDescription className="text-[#9ca3af]">
              {dlgHasImage
                ? "Type what to fix or change and we'll EDIT this exact image — only your words are sent to the AI, nothing else (perfect for correcting text/spelling on tags & signs). Leave blank to fully re-roll a brand-new image."
                : "Describe the image or leave blank to generate. Your channel style and selected characters are used."}
            </DialogDescription>
          </DialogHeader>
          {characters.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-[#9ca3af]">
                {dlgHasImage
                  ? "Characters to include (tap to toggle — pre-selected by name). Used when you re-roll a fresh image (leave the box below blank); ignored when editing with instructions."
                  : "Characters to include (tap to toggle — pre-selected by name):"}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="regen-character-chips">
                {characters.map((c) => {
                  const on = selChars.includes(c.image_id);
                  return (
                    <button
                      key={c.image_id}
                      data-testid={`regen-char-${c.image_id}`}
                      onClick={() => toggleChar(c.image_id)}
                      className={`flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                        on ? "border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff]" : "border-[#272a35] text-[#9ca3af] hover:text-[#f3f4f6]"
                      }`}
                    >
                      <img src={imageUrl(c.image_id)} alt={c.label} className="h-5 w-5 rounded-full object-cover" />
                      {c.label || "Character"}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <Textarea
            data-testid="regen-mods-input"
            value={mods}
            onChange={(e) => setMods(e.target.value)}
            placeholder={dlgHasImage
              ? 'e.g. correct the spelling of "Bella" on the pink dog tag'
              : "e.g. Make the background darker, add a surprised expression, leave space on the left for text"}
            className="min-h-[100px] resize-y bg-[#0e1015] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0"
          />
          <div className="rounded-md border border-[#272a35] bg-[#0e1015]/60 p-2">
              {promptView == null ? (
                <button
                  data-testid="view-exact-prompt-btn"
                  onClick={loadExactPrompt}
                  disabled={promptLoading}
                  className="flex items-center gap-1.5 text-xs text-[#00e5ff] hover:underline disabled:opacity-50"
                >
                  {promptLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
                  View / edit the exact prompt sent to the image AI
                </button>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#00e5ff]">Exact prompt sent (editable)</span>
                    <div className="flex items-center gap-2">
                      <button
                        data-testid="clear-exact-prompt-btn"
                        onClick={() => { setPromptView(""); requestAnimationFrame(() => exactPromptRef.current?.focus()); }}
                        title="Clear the prompt and type your own"
                        className="text-[#9ca3af] hover:text-[#ef4444]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid="refresh-exact-prompt-btn"
                        onClick={loadExactPrompt}
                        disabled={promptLoading}
                        title="Rebuild from the fields above"
                        className="text-[#9ca3af] hover:text-[#00e5ff]"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${promptLoading ? "animate-spin" : ""}`} />
                      </button>
                    </div>
                  </div>
                  <Textarea
                    data-testid="exact-prompt-input"
                    ref={exactPromptRef}
                    value={promptView}
                    onChange={(e) => setPromptView(e.target.value)}
                    className="min-h-[160px] resize-y bg-[#0b0c10] border-[#272a35] font-mono text-[11px] leading-relaxed text-[#f3f4f6] focus-visible:ring-[#00e5ff] focus-visible:ring-offset-0"
                  />
                  <p className="text-[10px] text-[#6b7280]">
                    {dlgHasImage
                      ? "This text is sent to gpt-image-1 together with the CURRENT image to edit it. Edit freely — your version is used as-is."
                      : "This EXACT text is sent to gpt-image-1. Edit it freely — your version is used as-is."}
                  </p>
                </div>
              )}
            </div>
          <div className="flex justify-end gap-2">
            <Button
              data-testid="cancel-regen-btn"
              variant="outline"
              onClick={() => setDlg(null)}
              className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-regen-btn"
              onClick={confirmRegen}
              className="rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!vdlg} onOpenChange={(o) => !o && setVdlg(null)}>
        <DialogContent className="border-[#272a35] bg-[#14161c] text-[#f3f4f6]" data-testid="video-regen-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Regenerate scene {(vdlg?.index ?? 0) + 1} video</DialogTitle>
            <DialogDescription className="text-[#9ca3af]">
              Describe any changes to the motion, camera, or action — or leave blank to just re-roll. The scene image and dialog are kept.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="video-regen-mods-input"
            value={vmods}
            onChange={(e) => setVmods(e.target.value)}
            placeholder="e.g. slower camera pan, character waves and smiles, add gentle wind, zoom in on the horse"
            className="min-h-[80px] resize-y bg-[#0e1015] border-[#272a35] text-[#f3f4f6] focus-visible:ring-[#a855f7] focus-visible:ring-offset-0"
          />
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">Model</label>
              <select
                data-testid="video-regen-model"
                value={vdlg?.model ?? "2.0"}
                onChange={(e) => setVdlg((v) => ({ ...v, model: e.target.value }))}
                className="w-full rounded border border-[#272a35] bg-[#0b0c10] px-2 py-1.5 text-xs text-[#f3f4f6] focus:outline-none focus:ring-1 focus:ring-[#a855f7]"
              >
                <option value="1.5">Seedance 1.5 Pro</option>
                <option value="2.0">Seedance 2.0</option>
                <option value="2.5">Seedance 2.5</option>
                <option value="grok">Grok Imagine</option>
                <option value="runway">Runway (Gen-4)</option>
            <option value="runway_seedance">Runway · Seedance 2.5</option>
            <option value="runway_hailuo">Runway · MiniMax 3</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">Duration</label>
              <select
                data-testid="video-regen-duration"
                value={vdlg?.duration ?? 15}
                onChange={(e) => setVdlg((v) => ({ ...v, duration: Number(e.target.value) }))}
                className="w-full rounded border border-[#272a35] bg-[#0b0c10] px-2 py-1.5 text-xs text-[#f3f4f6] focus:outline-none focus:ring-1 focus:ring-[#a855f7]"
              >
                <option value={5}>5s</option>
                <option value={8}>8s</option>
                <option value={10}>10s</option>
                <option value={12}>12s</option>
                <option value={15}>15s</option>
                <option value={20}>20s</option>
                <option value={30}>30s</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">Resolution</label>
              <select
                data-testid="video-regen-resolution"
                value={vdlg?.resolution ?? "1080p"}
                onChange={(e) => setVdlg((v) => ({ ...v, resolution: e.target.value }))}
                className="w-full rounded border border-[#272a35] bg-[#0b0c10] px-2 py-1.5 text-xs text-[#f3f4f6] focus:outline-none focus:ring-1 focus:ring-[#a855f7]"
              >
                <option value="480p">480p</option>
                <option value="720p">720p</option>
                <option value="1080p">1080p</option>
              </select>
            </div>
          </div>
          {vdlg?.duration === 15 && vdlg?.model === "1.5" && (
            <p className="text-[11px] text-amber-400" data-testid="video-regen-15s-warning">15s requires Seedance 2.0/2.5 or Grok Imagine — 1.5 Pro will be capped at 12s.</p>
          )}
          {vdlg?.duration > 15 && vdlg?.model !== "2.5" && (
            <p className="text-[11px] text-amber-400" data-testid="video-regen-30s-warning">Durations above 15s require Seedance 2.5 — other models will be capped.</p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              data-testid="cancel-video-regen-btn"
              variant="outline"
              onClick={() => setVdlg(null)}
              className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-video-regen-btn"
              onClick={confirmVideoRegen}
              className="rounded-md bg-[#a855f7] text-white hover:bg-[#9333ea] font-semibold"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Regenerate video
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
